import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Loader2, Lock, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function SuperAdminLoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  // Force dark mode
  useEffect(() => {
    const root = document.documentElement;
    const prev = root.classList.contains('dark') ? 'dark' : 'light';
    root.classList.remove('light');
    root.classList.add('dark');
    return () => { root.classList.remove('dark'); root.classList.add(prev); };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post('/superadmin/login', { email, password });
      localStorage.setItem('vs_token', data.token);
      localStorage.setItem('vs_superadmin', 'true');
      toast.success('Welcome Back');
      navigate('/superadmin');
    } catch (err) {
      console.error('Superadmin login error:', err.response?.data);
      toast.error(err.response?.data?.detail || 'Invalid credentials');
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="superadmin-login-page" className="min-h-screen flex items-center justify-center bg-background p-6">
      <Card className="w-full max-w-[400px] shadow-[var(--shadow-soft)]">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-foreground">
              <Lock className="h-6 w-6 text-background" />
            </div>
          </div>
          <CardTitle className="text-xl" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Platform Admin</CardTitle>
          <CardDescription>VendorSuite internal access only</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div><Label>Email</Label><Input data-testid="sa-email-input" type="email" value={email} onChange={e => setEmail(e.target.value)} required /></div>
            <div>
              <Label>Password</Label>
              <div className="relative">
                <Input data-testid="sa-password-input" type={showPassword ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)} required className="pr-10" />
                <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" onClick={() => setShowPassword(!showPassword)} tabIndex={-1}>
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <Button type="submit" data-testid="sa-login-button" className="w-full" disabled={loading}>
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Lock className="mr-2 h-4 w-4" />}
              {loading ? 'Authenticating...' : 'Sign In'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
